<script setup lang="ts">
import { computed } from 'vue'
import { assetUrl } from '../core/assets'

const props = defineProps<{ name: string; label?: string }>()
const src = computed(
  () => assetUrl(`ui.icon.${props.name}`) ?? assetUrl(props.name),
)

const paths: Record<string, string[]> = {
  dashboard: ['M3 11.5 12 4l9 7.5', 'M5 10.5V20h14v-9.5', 'M9 20v-6h6v6'],
  village: ['M4 20V9l5-4v4l6-5v6l5 3v7', 'M2 20h20', 'M8 13h2v3H8m7-2h2v3h-2'],
  buildings: [
    'M4 20V8h6v12M10 20V4h10v16',
    'M2 20h20',
    'M6.5 11h1m-1 3h1m5-6h2m-2 4h2m-2 4h2',
  ],
  construction: [
    'M4 19 15 8',
    'M14 5l5 5',
    'M3 21l4-1-3-3-1 4Z',
    'M13 4l2-2 7 7-2 2',
  ],
  map: ['M3 6l5-2 8 3 5-2v13l-5 2-8-3-5 2V6Z', 'M8 4v13m8-10v13'],
  military: ['M5 4l14 16M19 4 5 20', 'M7 6l-2-2m12 2 2-2m-4 14 2 2m-10-2-2 2'],
  army: ['M12 3 5 6v5c0 5 3 8 7 10 4-2 7-5 7-10V6l-7-3Z', 'M9 11l2 2 4-4'],
  recruitment: [
    'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
    'M2 21c0-4 3-7 7-7 2 0 4 1 5 2',
    'M18 8v6m-3-3h6',
  ],
  training: ['M12 3v18M3 12h18', 'M6 6l12 12m0-12L6 18'],
  movements: ['M4 7h12l-3-3m3 3-3 3', 'M20 17H8l3-3m-3 3 3 3'],
  attack: ['M4 20 18 6', 'M14 4h6v6', 'M6 16l2 2-3 3H2v-3l3-3'],
  support: ['M12 3 4 6v5c0 5 3.4 8.2 8 10 4.6-1.8 8-5 8-10V6l-8-3Z', 'M8 12h8'],
  espionage: [
    'M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z',
    'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z',
  ],
  reports: ['M5 3h11l3 3v15H5V3Z', 'M8 9h8M8 13h8M8 17h5', 'M16 3v4h4'],
  calculator: [
    'M6 3h12v18H6V3Z',
    'M8.5 7h7',
    'M9 11h1m4 0h1m-6 4h1m4 0h1m-6 4h1m4 0h1',
  ],
  research: [
    'M9 3h6M10 3v5l-5 9a3 3 0 0 0 3 4h8a3 3 0 0 0 3-4l-5-9V3',
    'M8 16h8',
  ],
  smithy: ['M4 8h16l-2 5H8L4 8Z', 'M10 13v6m4-6v6M7 21h10'],
  nobility: ['M3 8l4 4 5-7 5 7 4-4-2 11H5L3 8Z', 'M6 19h12'],
  economy: [
    'M12 3v18',
    'M17 7c0-2-2-3-5-3s-5 1-5 3 2 3 5 3 5 1 5 3-2 4-5 4-5-1-5-3',
  ],
  market: ['M4 9h16l-2-5H6L4 9Z', 'M6 9v10m12-10v10M3 19h18', 'M9 13h6'],
  transport: [
    'M3 7h11v9H3V7Z',
    'M14 10h4l3 3v3h-7',
    'M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm10 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  ],
  alliance: ['M5 21V4', 'M6 5h12l-3 4 3 4H6'],
  quests: ['M5 4h14v16H5V4Z', 'M8 9h8M8 13h5', 'M8 17l1.5 1.5L13 15'],
  events: [
    'M12 2l2.2 6.3H21l-5.4 3.9 2.1 6.3-5.7-3.8-5.7 3.8 2.1-6.3L3 8.3h6.8L12 2Z',
  ],
  inventory: ['M4 7h16v13H4V7Z', 'M7 7V4h10v3', 'M9 11h6'],
  rankings: ['M6 21V11h4v10H6Zm8 0V4h4v17h-4Zm-4 0V7h4v14h-4Z'],
  victory: [
    'M6 4h12v4c0 4-2 7-6 8-4-1-6-4-6-8V4Z',
    'M6 6H3v2c0 3 2 5 5 5m10-7h3v2c0 3-2 5-5 5',
    'M12 16v3m-4 2h8',
  ],
  messages: ['M3 5h18v13H7l-4 3V5Z', 'M7 9h10m-10 4h7'],
  chat: ['M4 4h16v12H9l-5 4V4Z', 'M8 9h.01m4 0h.01m4 0h.01'],
  friends: [
    'M8 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm9-1a3 3 0 1 0 0-6',
    'M2 21c0-4 2.5-7 6-7s6 3 6 7',
    'M15 14c3 0 5 2 5 5',
  ],
  notifications: ['M6 17h12l-2-3V9a4 4 0 0 0-8 0v5l-2 3Z', 'M10 20h4'],
  settings: [
    'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z',
    'M12 2v3m0 14v3M2 12h3m14 0h3M5 5l2 2m10 10 2 2M19 5l-2 2M7 17l-2 2',
  ],
  search: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z', 'M16 16l5 5'],
  profile: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z', 'M4 21c0-5 3-8 8-8s8 3 8 8'],
  worlds: [
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z',
    'M3 12h18M12 3c3 3 3 15 0 18m0-18c-3 3-3 15 0 18',
  ],
  admin: ['M5 20h14', 'M8 20v-8l4-9 4 9v8', 'M6 12h12'],
  more: ['M5 12h.01M12 12h.01M19 12h.01'],
  wood: ['M12 3v18', 'M12 8 7 12h10l-5-4-5 3h10l-5-4'],
  clay: ['M4 16c2-5 4-8 8-11 4 3 6 6 8 11-3 4-13 4-16 0Z'],
  iron: ['M7 5h10l4 7-4 7H7l-4-7 4-7Z'],
  population: [
    'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
    'M3 21c0-4 2-7 6-7s6 3 6 7',
  ],
  close: ['M5 5l14 14M19 5 5 19'],
  check: ['M4 12l5 5L20 6'],
  warning: ['M12 3 2 14H10L12 3Z', 'M12 9v4m0 3h.01'],
  info: ['M12 10v7m0-10h.01', 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z'],
  plus: ['M12 5v14M5 12h14'],
  minus: ['M5 12h14'],
  refresh: [
    'M20 7v5h-5',
    'M4 17v-5h5',
    'M6.5 8a7 7 0 0 1 11.5-2l2 1M17.5 16a7 7 0 0 1-11.5 2l-2-1',
  ],
  back: ['M19 12H5', 'M10 7l-5 5 5 5'],
  up: ['M12 19V5', 'M7 10l5-5 5 5'],
  down: ['M12 5v14', 'M7 14l5 5 5-5'],
  left: ['M19 12H5', 'M10 7l-5 5 5 5'],
  right: ['M5 12h14', 'M14 7l5 5-5 5'],
  home: ['M3 11.5 12 4l9 7.5', 'M5 10.5V20h14v-9.5', 'M9 20v-6h6v6'],
  'map-village': ['M4 20V10l8-6 8 6v10', 'M2 20h20', 'M9 20v-6h6v6'],
  'map-neutral': ['M12 3 21 12 12 21 3 12 12 3Z', 'M9 12h6'],
  favorite: [
    'M12 3l2.7 5.5 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1-4.4-4.3 6.1-.9L12 3Z',
  ],
}

const fallbackPaths = computed(
  () => paths[props.name] ?? ['M4 4h16v16H4V4Z', 'M8 8h8v8H8V8Z'],
)
</script>

<template>
  <span
    class="ui-icon"
    :aria-hidden="label ? undefined : 'true'"
    :aria-label="label"
  >
    <img v-if="src" :src="src" alt="" />
    <svg
      v-else
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linecap="round"
      stroke-linejoin="round"
      focusable="false"
      aria-hidden="true"
    >
      <path v-for="path in fallbackPaths" :key="path" :d="path" />
    </svg>
  </span>
</template>
